/**
 * GoalStrip — the locked goal, always visible. Ported from the OnFlow Design
 * System project (v2/components/track/GoalStrip.jsx).
 *
 * Appears on Home, Review and Profile so the user never has to remember what
 * they committed to — and so the deadline is always applying gentle pressure.
 */
import type { CSSProperties } from 'react';

import { ArrowRight } from './ArrowRight.js';
import { FlowStatus } from './FlowStatus.js';
import type { TrackState } from './FlowStatus.js';

interface GoalStripProps {
  goal: string;
  deadline?: string;
  daysLeft?: number;
  state?: TrackState;
  statusLabel?: string;
  /** 0-100. Omit to hide the progress hairline. */
  progress?: number;
  compact?: boolean;
  style?: CSSProperties;
}

/** Render a "start → end" range with a thin SVG arrow; plain string otherwise. */
function renderDeadline(deadline: string) {
  const parts = deadline.split(' → ');
  if (parts.length !== 2) return deadline;
  return (
    <>
      {parts[0]}
      <span style={{ margin: '0 5px', verticalAlign: 'middle', color: 'var(--ink-3)' }}>
        <ArrowRight size={12} />
      </span>
      {parts[1]}
    </>
  );
}

export function GoalStrip({
  goal,
  deadline = '',
  daysLeft,
  state = 'on-track',
  statusLabel,
  progress,
  compact = false,
  style,
}: GoalStripProps) {
  const accent =
    state === 'on-track'
      ? 'var(--on-track)'
      : state === 'drifting'
        ? 'var(--drifting)'
        : state === 'off-track'
          ? 'var(--off-track)'
          : 'var(--ink-2)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: compact ? '10px 14px' : '14px 16px',
        background: 'var(--paper-1)',
        border: '1px solid var(--edge)',
        borderRadius: 'var(--r-md)',
        boxShadow: 'var(--elev-1)',
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: compact ? 15 : 17,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--ink-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {goal}
        </div>
        <div
          style={{
            marginTop: 3,
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            letterSpacing: '0.04em',
            color: 'var(--ink-3)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {renderDeadline(deadline)}
          {daysLeft !== undefined && (
            <>
              {' · '}
              <span style={{ color: daysLeft <= 14 ? accent : 'var(--ink-2)' }}>
                {daysLeft} days left
              </span>
            </>
          )}
        </div>
        {progress !== undefined && (
          <div
            style={{
              marginTop: 8,
              height: 3,
              borderRadius: 999,
              background: 'var(--paper-3)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.max(0, Math.min(100, progress))}%`,
                background: accent,
                transition: 'width var(--t-arc) var(--ease-out)',
              }}
            />
          </div>
        )}
      </div>
      <FlowStatus state={state} label={statusLabel} />
    </div>
  );
}
